import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import Table from '../../components/Table';
import { Form, InputGroup, DropdownButton, Dropdown } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as apicall from '../../components/Apicall';

// Status badge styled with the portal's primary theme colour.
export const getStatusBadge = (status) => {
  const map = {
    DRAFT: { bg: '#eef0f2', color: '#555' },
    SCHEDULED: { bg: '#e8f0fe', color: '#1967d2' },
    QUEUED: { bg: '#fff4e5', color: '#b26a00' },
    RUNNING: { bg: '#fff4e5', color: '#b26a00' },
    COMPLETED: { bg: '#e6f4ea', color: '#2e7d32' },
    PARTIAL_SUCCESS: { bg: '#fdf3e7', color: '#b26a00' },
    FAILED: { bg: '#fce8ec', color: '#d01c4c' },
    CANCELLED: { bg: '#fce8ec', color: '#d01c4c' },
  };
  const s = map[status] || map.DRAFT;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: 500 }}>
      {status || 'DRAFT'}
    </span>
  );
};

const MarketingCampaignList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState(location.state?.searchTerm || '');
  const [statusFilter, setStatusFilter] = useState(location.state?.statusFilter || null);
  const [channelFilter, setChannelFilter] = useState(location.state?.channelFilter || null);
  const [filterApplied, setFilterApplied] = useState(Boolean(location.state?.statusFilter || location.state?.channelFilter));
  const [showFilters, setShowFilters] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = ['Campaign Name', 'Channel', 'Type', 'Country', 'Audience', 'Send Mode', 'Mode', 'Status'];

  const statusList = ['DRAFT', 'SCHEDULED', 'QUEUED', 'RUNNING', 'COMPLETED', 'PARTIAL_SUCCESS', 'FAILED', 'CANCELLED'];
  const channelList = ['SMS', 'WHATSAPP', 'PUSH'];

  const getCampaignsList = async (data) => {
    setLoading(true);
    try {
      const res = await apicall.post(data, 'get_all_campaigns');
      if (res.success) setCampaigns(res.data);
      else toast.error('Failed to fetch campaigns.');
    } catch (e) {
      toast.error(e.message.replace('Error:', ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    const data = {};
    if (statusFilter) data.status = statusFilter;
    if (channelFilter) data.channel = channelFilter;
    if (searchTerm !== '') data.keyword = searchTerm.trim().toLowerCase();
    setFilterApplied(Boolean(statusFilter || channelFilter));
    getCampaignsList(data);
  };

  const clearAllFilters = () => {
    setStatusFilter(null);
    setChannelFilter(null);
    setFilterApplied(false);
    setShowFilters(false);
    const data = {};
    if (searchTerm !== '') data.keyword = searchTerm.trim().toLowerCase();
    getCampaignsList(data);
  };

  const handleDetails = (index) => {
    const campaign = campaigns[index];
    navigate(`/marketing-campaignDetails/${campaign.id}`, {
      state: { campaignId: campaign.id, searchTerm, statusFilter, channelFilter, filterApplied },
    });
  };

  const handleEdit = (index) => {
    const campaign = campaigns[index];
    navigate(`/marketing-campaign/${campaign.id}`, {
      state: { campaignId: campaign.id, searchTerm, statusFilter, channelFilter, filterApplied },
    });
  };

  const campaignsData = campaigns.map((c) => [
    c.name,
    c.channel,
    c.campaign_type,
    c.country_name || 'United Arab Emirates',
    c.audience_type === 'ALL_CUSTOMERS' ? 'All Customers' : 'User List',
    c.send_mode === 'SCHEDULED' ? 'Scheduled' : 'Send Now',
    c.is_live ? 'Live' : 'Test',
    getStatusBadge(c.status),
  ]);

  return (
    <div className="vendor-table-container container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Marketing Campaigns</h3>
          <p>Compose and send SMS, WhatsApp and Push campaigns</p>
        </div>

        <div className="mx-5 mb-4">
          <InputGroup>
            <Form.Control
              type="search"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '300px' }}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            />
            <InputGroup.Text className="primaryBackground" style={{ cursor: 'pointer' }} onClick={applyFilters}>
              <FaSearch className="text-white" />
            </InputGroup.Text>
          </InputGroup>
        </div>

        <div className="d-flex align-items-center mb-3">
          <button
            className="btn Button primaryColorText btn-outline-secondary me-2 no-hover-effect"
            onClick={() => setShowFilters((p) => !p)}
          >
            {filterApplied ? 'Filters •' : 'Filters'}
          </button>
          <button className="btn Button primaryBackground text-white" style={{ whiteSpace: 'nowrap' }} onClick={() => navigate('/marketing-campaign/0')}>
            Add Campaign
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="d-flex align-items-center mb-3 gap-3">
          <DropdownButton id="status-filter" variant="outline-secondary" title={statusFilter || 'Status'}>
            {statusList.map((s) => (
              <Dropdown.Item key={s} onClick={() => setStatusFilter(s)}>{s}</Dropdown.Item>
            ))}
          </DropdownButton>
          <DropdownButton id="channel-filter" variant="outline-secondary" title={channelFilter || 'Channel'}>
            {channelList.map((c) => (
              <Dropdown.Item key={c} onClick={() => setChannelFilter(c)}>{c}</Dropdown.Item>
            ))}
          </DropdownButton>
          <button className="btn Button primaryBackground text-white" onClick={applyFilters}>Apply</button>
          <button className="btn Button text-black primaryColorText btn-outline-secondary no-hover-effect" onClick={clearAllFilters}>Clear All</button>
        </div>
      )}

      {loading ? (
        <Skeleton count={5} height={40} />
      ) : (
        <Table
          headers={headers}
          data={campaignsData}
          pagination={true}
          recordsCount={25}
          details={handleDetails}
          edit={handleEdit}
        />
      )}
    </div>
  );
};

export default MarketingCampaignList;
